import { fetchWithChatFeedback } from './chatFeedbackRequired';
import type {
  BrandNameJaArtifact,
  BrandNameJaCompletion,
  BrandNameJaProjection,
} from '../types/marketResearchBrandNameJa';

export type BrandNameJaApiErrorKind = 'conflict' | 'stale' | 'inactive' | 'validation'
  | 'forbidden' | 'not_found' | 'server' | 'network' | 'download';

export class BrandNameJaApiError extends Error {
  constructor(
    public readonly kind: BrandNameJaApiErrorKind,
    public readonly status: number,
    message: string,
    public readonly fieldErrors: Record<string, string> = {},
    public readonly currentRevision?: number,
  ) {
    super(message);
    this.name = 'BrandNameJaApiError';
  }
}

const genericMessage = 'We could not complete that J&A request. Your entries remain in this browser.';
const apiUrl = (endpoint: string): string => `${process.env.REACT_APP_API_URL || ''}${endpoint}`;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const fieldErrorsFrom = (value: unknown): Record<string, string> => {
  if (isRecord(value)) {
    return Object.fromEntries(Object.entries(value).filter((entry): entry is [string, string] => typeof entry[1] === 'string'));
  }
  if (!Array.isArray(value)) return {};
  const result: Record<string, string> = {};
  value.forEach((item) => {
    if (!isRecord(item)) return;
    const field = typeof item.field === 'string' ? item.field.replace(/^artifact\./, '') : '';
    if (field && typeof item.message === 'string') result[field] = item.message;
  });
  return result;
};

const errorFromResponse = async (response: Response): Promise<BrandNameJaApiError> => {
  const payload = await response.json().catch(() => ({}));
  const detail = isRecord(payload) ? payload.detail : null;
  const structured = isRecord(detail) ? detail : {};
  const code = typeof structured.code === 'string' ? structured.code : '';
  const fieldErrors = fieldErrorsFrom(structured.errors);
  const currentRevision = typeof structured.current_revision === 'number' ? structured.current_revision : undefined;
  let kind: BrandNameJaApiErrorKind = response.status >= 500 ? 'server' : 'network';
  let message = genericMessage;
  if (response.status === 403) {
    kind = 'forbidden';
    message = 'You do not have access to edit this J&A.';
  } else if (response.status === 404) {
    kind = 'not_found';
    message = 'The saved J&A could not be found.';
  } else if (response.status === 422) {
    kind = 'validation';
    message = 'Review the highlighted fields and try again.';
  } else if (response.status === 409) {
    kind = code === 'revision_conflict' ? 'conflict' : code === 'stale_source' ? 'stale' : 'inactive';
    message = kind === 'conflict'
      ? 'This J&A changed in another session. Your local edits have not been discarded.'
      : kind === 'stale'
        ? 'Product Discovery changed. Review this J&A before continuing.'
        : 'Brand-name use is no longer active for this workflow.';
  }
  return new BrandNameJaApiError(kind, response.status, message, fieldErrors, currentRevision);
};

const requestProjection = async (
  endpoint: string,
  method: 'GET' | 'PUT' | 'POST',
  body?: unknown,
): Promise<BrandNameJaProjection> => {
  let response: Response;
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 30_000);
  try {
    response = await fetchWithChatFeedback(apiUrl(endpoint), {
      method,
      credentials: 'include',
      signal: controller.signal,
      ...(body === undefined ? {} : {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }),
    });
  } catch {
    throw new BrandNameJaApiError('network', 0, genericMessage);
  } finally {
    window.clearTimeout(timeout);
  }
  if (!response.ok) throw await errorFromResponse(response);
  const envelope = await response.json().catch(() => null);
  if (!isRecord(envelope) || envelope.success !== true || !isRecord(envelope.data)) {
    throw new BrandNameJaApiError('server', response.status, genericMessage);
  }
  return envelope.data as unknown as BrandNameJaProjection;
};

export const getMarketResearchBrandNameJa = (flowId: string): Promise<BrandNameJaProjection> =>
  requestProjection(`/api/market-research/brand-name-ja?flow_id=${encodeURIComponent(flowId)}`, 'GET');

export const saveMarketResearchBrandNameJaDraft = (
  flowId: string,
  revision: number,
  artifact: BrandNameJaArtifact,
): Promise<BrandNameJaProjection> => requestProjection(
  '/api/market-research/brand-name-ja/draft',
  'PUT',
  { flow_id: flowId, revision, artifact },
);

/** Explicit generation only; never retry the writer from this client. */
export const generateMarketResearchBrandNameJa = (
  flowId: string,
  revision: number,
  artifact: BrandNameJaArtifact,
  answers: Record<string, string>,
  force: boolean,
): Promise<BrandNameJaProjection> => requestProjection(
  '/api/market-research/brand-name-ja/generate', 'POST',
  { flow_id: flowId, revision, artifact, answers, force },
);

export const rebaseMarketResearchBrandNameJaSource = (
  flowId: string,
  revision: number,
): Promise<BrandNameJaProjection> => requestProjection(
  '/api/market-research/brand-name-ja/rebase-source',
  'POST',
  { flow_id: flowId, revision },
);

export const completeMarketResearchBrandNameJa = async (
  flowId: string,
  revision: number,
  artifact: BrandNameJaArtifact,
): Promise<BrandNameJaCompletion> => {
  const result = await requestProjection(
    '/api/market-research/brand-name-ja/complete',
    'POST',
    { flow_id: flowId, revision, artifact },
  );
  if (result.post_ja_destination_stage === null || !Array.isArray(result.downstream_products)) {
    throw new BrandNameJaApiError('server', 200, genericMessage);
  }
  return result as BrandNameJaCompletion;
};

export const downloadMarketResearchBrandNameJaPdf = async (flowId: string): Promise<void> => {
  let response: Response;
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 60_000);
  try {
    response = await fetchWithChatFeedback(apiUrl(`/api/market-research/brand-name-ja/pdf?flow_id=${encodeURIComponent(flowId)}`), {
      method: 'GET', credentials: 'include', signal: controller.signal,
    });
  } catch {
    throw new BrandNameJaApiError('network', 0, genericMessage);
  } finally {
    window.clearTimeout(timeout);
  }
  if (!response.ok) throw await errorFromResponse(response);
  const contentType = response.headers.get('Content-Type')?.split(';')[0].trim().toLowerCase();
  const blob = await response.blob();
  const buffer = await new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new BrandNameJaApiError('download', response.status, 'The J&A PDF response could not be read.'));
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.readAsArrayBuffer(blob);
  });
  const bytes = new Uint8Array(buffer);
  if (contentType !== 'application/pdf' || bytes.length < 5
      || String.fromCharCode(...Array.from(bytes.slice(0, 5))) !== '%PDF-') {
    throw new BrandNameJaApiError('download', response.status, 'The J&A PDF response was not valid.');
  }
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const candidate = disposition.match(/filename="?([^";]+)"?/i)?.[1] ?? `brand-name-ja-${flowId}.pdf`;
  const filename = candidate.replace(/[^A-Za-z0-9._-]/g, '_');
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};
